/*
 * A commander's prospecting samples under way (`intel.sampling`, reported
 * every 5 s, worked in steps every 30 s): a prospector stopped in a cell
 * builds a soil sample (2 min) or a deep core (20 min). From when a
 * sample last moved it's taken to go on at the game's pace (ten ticks a
 * second).
 */

export interface Sample {
  cell: number;
  kind: "Soil" | "Core";
  ticks: number;
  need: number;
}

let samples: (Sample & { at: number })[] = [];
/** The intel grid: cells across, and hexes a cell side. */
let grid = { cols: 0, cell: 16 };

export function setSampling(state: Record<string, unknown>, cols: number, cell: number): void {
  const now = performance.now();
  samples = ((state.intel as { sampling?: Sample[] } | undefined)?.sampling ?? [])
    .filter((s) => s.need > 0)
    .map((s) => {
      // Unchanged since the last report: still counting from when it moved.
      const was = samples.find((x) => x.cell === s.cell && x.kind === s.kind);
      return { ...s, at: was && was.ticks === s.ticks ? was.at : now };
    });
  grid = { cols, cell };
}

export const samplingGrid = () => grid;

/** The cell holding hex (q, r). */
export function cellOf(q: number, r: number): number {
  return Math.floor(r / grid.cell) * grid.cols + Math.floor(q / grid.cell);
}

/** The samples now: done (0..1) and seconds left, at the pace since the
 *  last report. */
export function sampling(): (Sample & { done: number; left: number })[] {
  const now = performance.now();
  return samples.map((s) => {
    const ticks = Math.min(s.need, s.ticks + ((now - s.at) / 1000) * 10);
    return { ...s, done: ticks / s.need, left: Math.max(0, (s.need - ticks) / 10) };
  });
}

/** A cell where the side last saw forces (`intel.seen`): when (tick) and
 *  how many of each side. */
export interface Seen {
  cell: number;
  tick: number;
  forces: [number, number][];
  /** Seen by another side's units and shared with you (a feed, an
   *  agreement): whose. Absent: your own. */
  from?: number;
}

let seen: Seen[] = [];

export function setSeen(state: Record<string, unknown>): void {
  seen = (state.intel as { seen?: Seen[] } | undefined)?.seen ?? [];
}

export const seenCells = () => seen;

/** "1:20". */
export function minutes(seconds: number): string {
  const s = Math.ceil(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
